import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { useRescanOutages, useTaskPolling } from "../hooks";
import type { EstimateJobDetail, JobOutages } from "../types";

const STATUS_STYLE: Record<
  JobOutages["status"],
  { label: string; cls: string; title: string }
> = {
  planned: {
    label: "Planowane wyłączenia",
    cls: "border-amber-300 bg-amber-50 text-amber-900",
    title: "Dokumentacja przewiduje wyłączenia zasilania na czas robót",
  },
  not_planned: {
    label: "Bez wyłączeń",
    cls: "border-blue-300 bg-blue-50 text-blue-900",
    title: "Dokumentacja nie przewiduje wyłączeń — roboty pod napięciem / PPN",
  },
  unknown: {
    label: "Brak informacji o wyłączeniach",
    cls: "border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)]",
    title: "W przesłanych dokumentach nie znaleziono warunków wyłączeń",
  },
};

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="section-label">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-[var(--color-text-primary)] tabular-nums">
        {value}
      </div>
    </div>
  );
}

/** Warunki wyłączeń zasilania — nie ma ich w przedmiarze, są w specyfikacji
 *  (SWZ / OPZ / STWiORB / umowa). Wpływają na organizację robót, więc stoją
 *  przy wycenie razem z cytatami, na podstawie których AI je odczytało. */
export function OutagesCard({
  job,
  canEdit,
}: {
  job: EstimateJobDetail;
  canEdit: boolean;
}) {
  const data = job.outages as JobOutages;
  const hasData = !!data && !!data.status;
  const status = hasData ? data.status : "unknown";
  const style = STATUS_STYLE[status];
  const [open, setOpen] = useState(false);
  const rescan = useRescanOutages();
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const qc = useQueryClient();

  useEffect(() => {
    const s = task.data?.status;
    if (s && ["success", "failure", "cancelled"].includes(s)) {
      if (s === "success") qc.invalidateQueries({ queryKey: ["est", "job", job.id] });
      setTaskId(null);
    }
  }, [task.data?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const citations = (data?.documents ?? []).flatMap((d) =>
    (d.citations ?? []).map((c) => ({ ...c, filename: d.filename })),
  );

  return (
    <div className="card">
      <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">
            Wyłączenia zasilania
          </span>
          <span
            className={`px-2 py-0.5 text-xs font-medium border ${style.cls}`}
            title={style.title}
          >
            {style.label}
          </span>
          {data?.conflict && (
            <span
              className="px-2 py-0.5 text-xs font-medium border border-red-300 bg-red-50 text-red-800"
              title="Dokumenty podają różne limity — pokazujemy najostrzejszy"
            >
              ⚠ rozbieżność w dokumentach
            </span>
          )}
        </div>
        {canEdit && (
          <Button
            variant="secondary"
            size="sm"
            loading={!!taskId || rescan.isPending}
            onClick={async () => {
              const r = await rescan.mutateAsync(job.id);
              setTaskId(r.task_id);
            }}
            title="Przeszukaj dokumenty jeszcze raz — np. po dorzuceniu specyfikacji"
          >
            {taskId ? "Szukam…" : hasData ? "Szukaj ponownie" : "Sprawdź wyłączenia"}
          </Button>
        )}
      </div>

      <div className="p-4 space-y-3">
        {status === "planned" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Fact
              label="Dozwolony czas wyłączenia"
              value={
                data.max_hours_per_outage
                  ? `${data.max_hours_per_outage} h`
                  : "nie podano"
              }
            />
            {data.total_hours && (
              <Fact label="Łącznie" value={`${data.total_hours} h`} />
            )}
            {data.max_outages != null && (
              <Fact label="Liczba wyłączeń" value={`maks. ${data.max_outages}`} />
            )}
            {data.notice_days && (
              <Fact label="Zgłoszenie" value={`${data.notice_days} dni wcześniej`} />
            )}
          </div>
        )}

        {status === "planned" && !data.max_hours_per_outage && (
          <div className="text-[11px] text-amber-700">
            ⚠ Dokumenty mówią o wyłączeniach, ale nie podają limitu godzin — ustal go
            z zamawiającym przed wyceną robót w oknie wyłączeniowym.
          </div>
        )}

        {data?.note && (
          <div className="text-xs text-[var(--color-text-secondary)]">{data.note}</div>
        )}
        {data?.window && (
          <div className="text-xs text-[var(--color-text-secondary)]">
            <span className="font-medium">Okno czasowe:</span> {data.window}
          </div>
        )}
        {data?.penalty && (
          <div className="text-xs text-amber-700">
            <span className="font-medium">Kary za przekroczenie:</span> {data.penalty}
          </div>
        )}

        {status === "unknown" && (
          <div className="text-xs text-[var(--color-text-secondary)]">
            {hasData
              ? `Przeszukano ${data.scanned_documents} dokument(ów) — nie znaleziono warunków wyłączeń. Jeśli specyfikacji nie było w komplecie, dorzuć ją i kliknij „Szukaj ponownie”.`
              : "Ta wycena powstała zanim czytaliśmy warunki wyłączeń. Kliknij „Sprawdź wyłączenia”, żeby przeszukać dokumenty."}
          </div>
        )}

        {citations.length > 0 && (
          <div>
            <button
              className="text-[11px] text-[var(--color-primary)] hover:underline"
              onClick={() => setOpen((o) => !o)}
            >
              {open
                ? "Ukryj cytaty ze specyfikacji"
                : `Pokaż cytaty ze specyfikacji (${citations.length})`}
            </button>
            {open && (
              <ul className="mt-2 space-y-2">
                {citations.map((c, i) => (
                  <li
                    key={i}
                    className="border-l-2 border-[var(--color-border)] pl-3 text-xs"
                  >
                    <div className="text-[var(--color-text-primary)] italic">
                      „{c.quote}”
                    </div>
                    <div className="text-[10px] text-[var(--color-text-muted)]">
                      {c.filename}
                      {c.page ? `, s. ${c.page}` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {taskId && (
          <div className="text-[11px] text-[var(--color-text-muted)] whitespace-pre-wrap">
            {task.data?.log?.split("\n").slice(-2).join("\n") || "Uruchamiam…"}
          </div>
        )}
      </div>
    </div>
  );
}
